import React, { useMemo, useState, useCallback } from 'react';
import { Translate } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import Creatable from 'react-select/creatable';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import XIcon from '../../resources/svg/icon_remove_row.svg';
import css from './NewTagInput.css';
import TagInputSuggestion from './TagInputSuggestion';
import SelectMenuList from './SelectMenuList';
import SelectGroup from './SelectGroup';
import Select from './Select';
import Icon, { IconSizes, IconTypes } from '../foundations/Icon';

const ControlComponent = ({ selectProps, ...restProps }) => {
    const { headerIcon, headerText, isDisabled } = selectProps;
    return (
        <div className={css.tagInputControl}>
            {headerText && (
                <span className={classNames(css.headerContainer, { [css.isDisabled]: isDisabled })}>
                    <span className={css.headerIcon}>{headerIcon} </span>
                    <span className={css.headerText}>
                        <Translate id={headerText} />
                    </span>
                    <div className={css.verticalLine} />
                </span>
            )}
            <components.Control {...restProps} />
        </div>
    );
};

ControlComponent.propTypes = {
    selectProps: PropTypes.shape({
        headerIcon: PropTypes.element,
        headerText: PropTypes.string,
        isDisabled: PropTypes.string,
    }).isRequired,
};

function Tag({ data, removeProps, selectProps }) {
    const showDelete = data.deleteEnabled === true || data.deleteEnabled === undefined;

    const onMouseDown = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    const title = showDelete ? '' : selectProps.tagLockAlt || '';

    return (
        <div className={css.tagContainer} title={title} onMouseDown={onMouseDown}>
            {data.icon && <div className={css.icon}>{data.icon}</div>}
            {data.subTitle && <div className={css.subTitle}>{data.subTitle}</div>}
            <div className={classNames(css.tagLabel, selectProps.tagLabelClass)}>{data.label}</div>
            {showDelete && (
                <div className={css.tagX} role="button" {...removeProps} tabIndex={0}>
                    <XIcon className={css.clearIcon} />
                </div>
            )}
            {!showDelete && (
                <Icon name="lock-line" size={IconSizes.X_SMALL} type={IconTypes.MINGCUTE} className={css.tagLock} />
            )}
        </div>
    );
}

Tag.propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
    removeProps: PropTypes.objectOf(PropTypes.any).isRequired,
};

function DraggableTag(props) {
    const { data, selectProps } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: data.value,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Tag {...props} />
        </div>
    );
}

DraggableTag.propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
};

const DEFAULT_PLACEHOLDER = 'STATIC.PLACEHOLDERS.SELECT_FILTER';

function NewTagInput({
    tags = [],
    options = [],
    disabled = false,
    containerStyle = {},
    placeholder = DEFAULT_PLACEHOLDER,
    tagLockAlt = '',
    error = null,
    creatable = false,
    openMenuOnClick = true,
    sortable = false,
    onChange = () => {},
    virtualScrolling = false,
    className = '',
    isMulti = true,
    isClearable = false,
    dropdownIndicatorStyles = () => {},
    placeholderClass = '',
    isSearchable = true,
    separator = true,
    tagLabelClass,
    headerIcon,
    headerText,
    onEditClick,
    onDeleteClick,
    hideSelectedOptions,
    tagsLimit,
    tagsLimitMessage = "You've reached the maximum",
    noOptionsMessage,
    dataTestId,
}) {
    const SelectClass = useMemo(() => {
        return !creatable ? Select : Creatable;
    }, [creatable]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 4,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback(
        event => {
            const { active, over } = event;

            if (active && over && active.id !== over.id) {
                const oldIndex = tags.findIndex(tag => tag.value === active.id);
                const newIndex = tags.findIndex(tag => tag.value === over.id);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newTags = arrayMove(tags, oldIndex, newIndex);
                    onChange(newTags);
                }
            }
        },
        [tags, onChange]
    );

    const componentsOverride = { Option: TagInputSuggestion };
    const styles = { dropdownIndicator: dropdownIndicatorStyles };

    if (isMulti) {
        componentsOverride.MultiValue = sortable ? DraggableTag : Tag;
    }

    if (headerText) {
        componentsOverride.Control = ControlComponent;
    }

    if (virtualScrolling) {
        componentsOverride.MenuList = SelectMenuList;
        componentsOverride.Group = SelectGroup;
    }
    if (!separator) {
        componentsOverride.IndicatorSeparator = null;
    }

    // So component is always searchable - especially in single value mode
    const [input, _setInput] = useState('');
    const setInput = useCallback(val => _setInput(val), []);

    const onFocus = useCallback(() => {
        if (!isMulti && tags.length) {
            setInput(tags[0].label);
        }
    }, [isMulti, tags]);

    const filteredOptionsByInput = useMemo(() => {
        return {};
    }, [options]);

    const getFilteredOptionsCallback = (option, selectInput, result, currentGroup) => {
        const { label, subLabel = '' } = option;
        const optionValue = label.toLowerCase();
        const optionSubLabelValue = subLabel.toLowerCase();
        const labelSearchResult = optionValue.includes(selectInput.toLowerCase());
        const subLabelSearchResult = optionSubLabelValue.includes(selectInput.toLowerCase());

        if (labelSearchResult || subLabelSearchResult) {
            result.add(optionValue);

            if (currentGroup) {
                result.add(currentGroup);
            }
        }
    };

    // The function is getting an input value to filter by,
    // and return a set of all options and groups that includes the input value.
    const getFilteredOptions = selectInput => {
        const result = new Set();
        let currentGroup = null;

        options.forEach(option => {
            if (option.isDisabled) {
                currentGroup = option.label.toLowerCase();
                return;
            }
            if (option.options) {
                const groupOptions = option.options;
                groupOptions.forEach(groupOption => {
                    getFilteredOptionsCallback(groupOption, selectInput, result, currentGroup);
                });
            }

            getFilteredOptionsCallback(option, selectInput, result, currentGroup);
        });

        return result;
    };

    // A custom filter method for react-select.
    const filterOptions = (option, selectInput) => {
        if (!selectInput) {
            return true;
        }

        if (!filteredOptionsByInput[selectInput]) {
            filteredOptionsByInput[selectInput] = getFilteredOptions(selectInput);
        }

        return filteredOptionsByInput[selectInput].has(option.label.toLowerCase());
    };

    const selectComponent = (
        <SelectClass
            dataTestId={dataTestId}
            options={!tagsLimit || tags.length < tagsLimit ? options : []}
            noOptionsMessage={() => {
                return tags.length === tagsLimit ? tagsLimitMessage : noOptionsMessage;
            }}
            hideSelectedOptions={hideSelectedOptions}
            isClearable={isClearable}
            isDisabled={disabled}
            isMulti={isMulti}
            onChange={onChange}
            className={classNames(css.singularSelect, placeholderClass, error && css.error)}
            escapeClearsValue={false}
            classNamePrefix="Select"
            components={componentsOverride}
            tagLockAlt={tagLockAlt}
            isSearchable={isSearchable}
            openMenuOnClick={openMenuOnClick}
            styles={styles}
            // Allow for single valued input to always be searchable
            // https://stackoverflow.com/questions/64298547/dont-clear-input-on-select-using-react-select
            placeholder={
                isMulti || !tags || placeholder !== DEFAULT_PLACEHOLDER ? (
                    <Translate id={placeholder} />
                ) : (
                    tags.label
                )
            }
            value={tags}
            inputValue={isMulti ? undefined : input} // allows you continue where you left off
            onInputChange={isMulti ? undefined : setInput} // allows for actually typing
            onFocus={isMulti ? undefined : onFocus}
            blurInputOnSelect={!isMulti} // actually allows for ^^ to work
            filterOption={filterOptions}
            tagLabelClass={tagLabelClass}
            headerIcon={headerIcon}
            headerText={headerText}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
        />
    );

    return (
        <div
            className={`${className || ''} ${css.container} ${disabled ? css.disabled : ''} fs-ignore-rage-clicks`}
            style={{ ...containerStyle }}
        >
            {sortable && isMulti && tags.length > 1 ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={tags.map(tag => tag.value)} strategy={horizontalListSortingStrategy}>
                        {selectComponent}
                    </SortableContext>
                </DndContext>
            ) : (
                selectComponent
            )}
            {error && (
                <div className={css.errorMessage}>
                    <Translate id={error} />
                </div>
            )}
        </div>
    );
}

NewTagInput.propTypes = {
    onChange: PropTypes.func,
    tags: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    containerStyle: PropTypes.objectOf(PropTypes.any),
    className: PropTypes.string,
    tagLockAlt: PropTypes.string,
    error: PropTypes.string,
    virtualScrolling: PropTypes.bool,
    creatable: PropTypes.bool,
    openMenuOnClick: PropTypes.bool,
    sortable: PropTypes.bool,
    isMulti: PropTypes.bool,
    dropdownIndicatorStyles: PropTypes.func,
    placeholderClass: PropTypes.string,
    isSearchable: PropTypes.bool,
    separator: PropTypes.bool,
    isClearable: PropTypes.bool,
    tagLabelClass: PropTypes.string,
    headerIcon: PropTypes.element,
    headerText: PropTypes.string,
    onEditClick: PropTypes.func,
    hideSelectedOptions: PropTypes.bool,
    tagsLimit: PropTypes.number,
    tagsLimitMessage: PropTypes.string,
    noOptionsMessage: PropTypes.string,
    dataTestId: PropTypes.string,
};

export default NewTagInput;
export const TAG_INPUT_VALUE_ACTIONS = {
    SELECT_OPTION: 'select-option',
    DESELECT_OPTION: 'deselect-option',
};
